<template>
  <div class="login-container">
    <div class="login-left">
      <img src="/logo.png" alt="Team CRM" class="brand-logo" />
      <div class="brand-notice">
        <p>This portal is for authorized team members only.</p>
        <p>All access and requests are monitored.</p>
      </div>
    </div>
    <div class="login-right">
      <div class="login-form-wrapper">
        <h2 class="form-title">Sign in to your account</h2>
        <p class="form-subtitle">Enter your credentials to continue</p>
        <div class="form-grid" @keydown.enter="handleLogin">
          <div class="form-row">
            <label class="form-label">Email</label>
            <TextInput v-model="email" type="email" placeholder="you@example.com" ref="emailInput" />
          </div>
          <div class="form-row">
            <label class="form-label">Password</label>
            <TextInput v-model="password" type="password" placeholder="Enter your password" />
          </div>
        </div>
        <button class="button primary form-button" :disabled="isLoading" @click="handleLogin">
          {{ isLoading ? 'Signing in...' : 'Sign In' }}
        </button>
        <div v-if="error" class="login-error">
          {{ error }}
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import TextInput from '@/components/inputs/TextInput.vue';
import * as api from '@/api/index.js';
import { alertService } from '@/libs/alert.js';

export default {
  name: 'LoginView',
  components: {
    TextInput
  },
  data() {
    return {
      email: '',
      password: '',
      isLoading: false,
      error: null
    };
  },
  mounted() {
    this.$nextTick(() => {
      this.$refs.emailInput?.$el?.focus();
    });
    this.checkIfAlreadyLoggedIn();
  },
  methods: {
    async checkIfAlreadyLoggedIn() {
      try {
        const practices = await api.fetchPractices();

        if (practices && practices.length > 0) {
          const currentPracticeId = api.getPracticeId();

          if (!currentPracticeId) {
            api.setPracticeId(practices[0].practiceId);
          }

          this.$router.push('/patients');
        }
      } catch (error) {
        // User is not authenticated, stay on login page
      }
    },
    async handleLogin() {
      this.error = null;

      if (!this.email || !this.email.trim()) {
        this.error = 'Email is required';
        return;
      }

      if (!this.password || !this.password.trim()) {
        this.error = 'Password is required';
        return;
      }

      this.isLoading = true;
      try {
        await api.login(this.email, this.password);

        try {
          const practices = await api.fetchPractices();
          if (practices && practices.length > 0) {
            const currentPracticeId = api.getPracticeId();
            if (!currentPracticeId) {
              api.setPracticeId(practices[0].practiceId);
            }
          }
        } catch (practiceError) {
          console.error('Failed to fetch practices:', practiceError);
        }

        alertService.success('Login successful!');
        this.$router.push('/patients');
      } catch (error) {
        this.error = error.message || 'Login failed. Please check your credentials.';
        this.isLoading = false;
      }
    }
  }
}
</script>

<style scoped>
.login-container {
  min-height: 100vh;
  display: flex;
}

.login-left {
  flex: 1;
  background: #1e3b5d;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 40px;
  gap: 32px;
}

.brand-logo {
  width: 330px;
  max-width: 100%;
  height: auto;
  display: block;
}

.brand-notice {
  text-align: center;
  color: rgba(255, 255, 255, 0.5);
  font-size: 12px;
  line-height: 1.5;
}

.brand-notice p {
  margin: 0;
}

.login-right {
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 40px;
  background: #f8f9fb;
}

.login-form-wrapper {
  width: 100%;
  max-width: 380px;
}

.form-title {
  font-size: 24px;
  font-weight: 600;
  color: #1a1a1a;
  margin: 0 0 6px;
}

.form-subtitle {
  font-size: 14px;
  color: #6b7280;
  margin: 0 0 32px;
}

.form-grid {
  display: flex;
  flex-direction: column;
  gap: 20px;
  margin-bottom: 28px;
}

.form-row {
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.login-error {
  margin-top: 16px;
  padding: 10px 12px;
  background: #fef2f2;
  border: 1px solid #fecaca;
  border-radius: var(--border-radius);
  color: #dc2626;
  font-size: 13px;
  text-align: center;
}

@media (max-width: 768px) {
  .login-container {
    flex-direction: column;
  }

  .login-left {
    flex: none;
    padding: 48px 24px;
  }

  .login-right {
    flex: 1;
    padding: 32px 24px;
  }
}
</style>
